import { openContextModal } from '@mantine/modals';
import { CollectionHelpers } from 'src/helpers/CollectionHelpers';
import { mutate } from 'swr';

import { publicConfig } from '@public-config';

import { ChatChannelAccessService } from '@diagram/services/ChatChannelAccessService';
import { SidebarService } from '@diagram/services/SidebarService';
import { chatState } from '@diagram/state/chat';
import { diagram } from '@diagram/state/diagram';
import { SidebarType } from '@diagram/state/sidebar';

import {
    CompanyProfile,
    DiagramChatChannel,
    DiagramChatChannelListParams,
    DiagramChatChannelType,
    DiagramChatMessage,
    DiagramComponentInstance,
    ListResponse,
    Manufacturer,
    PermissionDiagramGeneral,
} from 'models';
import { ChatHelpers } from 'helpers/ChatHelpers';
import { TipTapHelpers } from 'helpers/TipTapHelpers';

import { ApiService } from 'services/ApiService';
import { ImpersonationService } from 'services/ImpersonationService';
import { InternalTrackingService } from 'services/InternalTrackingService';
import { ModalService } from 'services/ModalService';
import { PayloadSDK } from 'services/PayloadSDK';
import { PermissionService } from 'services/PermissionService';
import { ProjectCollaboratorService } from 'services/ProjectCollaboratorService';
import { state as currentProjectState } from 'state/current-project';
import { currentTeamCompaniesState } from 'state/current-team-companies';
import { state as currentUserState } from 'state/current-user';

type BaseMessageArgs = {
    projectId: DiagramChatMessage['project'];
    diagramId: DiagramChatMessage['diagram'];
    channelId: DiagramChatMessage['channel'];
    parentId?: DiagramChatMessage['parent'];
    // The id of the company to attribute the message to; omit to send as user.
    // The server only keeps the attribution when the author belongs to that company.
    createdByCompany?: Manufacturer['id'];
};

const ChatService = {
    getChannels: async (projectId: DiagramChatChannel['project']) => {
        const { docs: channels } = await PayloadSDK.find({
            collection: 'diagramChatChannels',
            where: {
                project: {
                    equals: projectId,
                },
            },
            sort: 'name',
            pagination: false,
        });

        return {
            channels: channels as unknown as DiagramChatChannel[],
        };
    },

    refreshChannels: async (projectId: DiagramChatChannel['project']) => {
        await mutate(ChatHelpers.swr.channels(projectId));
    },

    openCreateChannelModal: () => {
        openContextModal({
            modal: 'createChatChannel',
            innerProps: {},
            withCloseButton: false,
        });
    },

    openInviteCompanyModal: (channel: DiagramChatChannel) => {
        ModalService.openContextModal({
            modal: 'inviteCompany',
            innerProps: { channel },
            withCloseButton: false,
            size: '480px',
        });
    },

    createChannel: async (
        projectId: DiagramChatChannel['project'],
        name: DiagramChatChannel['name'],
        type: DiagramChatChannel['type'] = DiagramChatChannelType.TEAM,
    ) => {
        const { doc: channel } = await ApiService.post<{
            doc: DiagramChatChannel;
        }>(
            `${publicConfig.urls.api}/diagramChatChannels`,
            {
                project: diagram.project,
                name,
                type,
            },
            { throwError: true },
        );

        InternalTrackingService.track('diagram.chat.channel.create', {
            project: projectId,
            channel: channel.id,
        });

        await ChatService.setOptimisticChannels(projectId, (channels) => {
            return [...channels, channel];
        });

        return channel;
    },

    createChannelForCompany: async (
        company: CompanyProfile,
        { componentInstance, message }: { componentInstance?: DiagramComponentInstance; message?: string } = {},
    ) => {
        await ChatService.whileConnecting({ company }, async (isCancelled) => {
            const [{ channels }, channelCompanies] = await Promise.all([
                ChatService.getChannels(diagram.project),
                ChatChannelAccessService.listCompanies(diagram.project),
            ]);

            if (isCancelled()) {
                return;
            }

            const existing = ChatHelpers.findCompanyChannel(channels, channelCompanies, [company.id]);

            if (existing) {
                if (message) {
                    ChatService.prefillMessage(message);
                }

                ChatService.activateChannel(existing.id, existing.type);

                return;
            }

            // Only team members create company channels; anyone else asks the team to bring the company in.
            if (currentProjectState.project?.permissionsAs !== 'user') {
                ModalService.openContextModal({
                    modal: 'confirmation',
                    innerProps: {
                        title: `Chat with ${company.name}`,
                        children: `You do not have permission to start a chat channel with ${company.name}. Ask one of the team members of this project to create the channel and add you to it.`,
                        confirm: () => ChatService.requestChannelForCompany(company),
                        confirmLabel: 'Request access',
                    },
                    withCloseButton: false,
                    size: '460px',
                });

                return;
            }

            ChatService.openAskCompanyModal({ company, componentInstance, message });
        });
    },

    openAskCompanyModal: (innerProps: {
        company: CompanyProfile;
        componentInstance?: DiagramComponentInstance;
        message?: string;
        channel?: DiagramChatChannel;
    }) => {
        ModalService.openContextModal({
            modal: 'askCompany',
            innerProps,
            withCloseButton: false,
            size: '520px',
        });
    },

    startCompanyChannel: async (company: Manufacturer, message?: string | object) => {
        InternalTrackingService.track('diagram.chat.ask-company.confirm', {
            company: company.name,
        });

        const channel = await ChatService.createChannel(
            diagram.project,
            ChatHelpers.transformChannelName(company.name),
            DiagramChatChannelType.MANUFACTURING_SUPPORT,
        );

        await ChatChannelAccessService.addCompany(channel, company.id);

        if (message) {
            ChatService.prefillMessage(message);
        }

        ChatService.activateChannel(channel.id, channel.type);
        ChatService.showAccessPopover();
    },

    whileConnecting: async (
        target: { company?: CompanyProfile },
        run: (isCancelled: () => boolean) => Promise<void>,
    ) => {
        let cancelled = false;

        const connecting = ModalService.openContextModal({
            modal: 'connectingWithCompany',
            innerProps: target,
            withCloseButton: false,
            centered: true,
            size: 'auto',
            onClose: () => {
                cancelled = true;
            },
        });

        try {
            await run(() => cancelled);
        } finally {
            ModalService.closeModal(connecting);
        }
    },

    // Opens the channel a non-team user shares with the project team, prefilled with a request to
    // invite the company.
    requestChannelForCompany: async (company: Manufacturer) => {
        const channel = await ChatService.getRequestsChannel();

        if (!channel) {
            ModalService.open({
                title: `Chat with ${company.name}`,
                children: `You do not have a channel with the team of this project yet. Contact one of the team members directly to get ${company.name} involved.`,
            });

            return;
        }

        InternalTrackingService.track('diagram.chat.invite-company.access-request', {
            channel: channel.name,
            company: company.name,
        });

        ChatService.prefillMessage(
            `Hi, could you invite **${company.name}** to this project? I would like to get their support on the components I am working with in this design.`,
        );
        ChatService.activateChannel(channel.id, channel.type);
    },

    // Only team members who manage the project invite collaborators; anyone else asks the team to do it.
    requestCollaboratorInvite: () => {
        ModalService.openContextModal({
            modal: 'confirmation',
            innerProps: {
                title: 'Invite collaborators',
                children:
                    'You do not have permission to invite people to this project. Ask one of the team members of this project to invite them for you.',
                confirm: ChatService.requestCollaboratorInviteInChannel,
                confirmLabel: 'Ask the team',
            },
            withCloseButton: false,
            size: '460px',
        });
    },

    requestCollaboratorInviteInChannel: async () => {
        const channel = await ChatService.getRequestsChannel();

        if (!channel) {
            ModalService.open({
                title: 'Invite collaborators',
                children:
                    'You do not have a channel with the team of this project yet. Contact one of the team members directly to invite someone.',
            });

            return;
        }

        InternalTrackingService.track('diagram.chat.invite-collaborator.access-request', {
            channel: channel.name,
        });

        ChatService.prefillMessage('Could you invite someone to collaborate on this project? Their email address is ');
        ChatService.activateChannel(channel.id, channel.type);
    },

    // The channel the current user shares with the project team: collaborators get their personal
    // channel, created on first use; company users get their company's channel.
    getRequestsChannel: async () => {
        const project = currentProjectState.project!;
        const user = currentUserState.user!;

        const { channels } = await ChatService.getChannels(project.id);

        if (project.permissionsAs === 'collaborator') {
            const channel = channels.find(
                (candidate) =>
                    candidate.type === DiagramChatChannelType.COLLABORATOR &&
                    CollectionHelpers.equals(candidate.createdBy, user.id),
            );

            if (channel) {
                return channel;
            }

            const created = await ChatService.createChannel(
                project.id,
                ChatHelpers.transformChannelName(user.name!),
                DiagramChatChannelType.COLLABORATOR,
            );

            await ChatChannelAccessService.addUser(created, user.id);

            return created;
        }

        if (project.permissionsAs === 'company') {
            const companyIds = CollectionHelpers.ids(currentTeamCompaniesState.companies);
            const channelCompanies = await ChatChannelAccessService.listCompanies(project.id);

            return ChatHelpers.findCompanyChannel(channels, channelCompanies, companyIds);
        }

        return undefined;
    },

    addCompanyToChannel: async (channel: DiagramChatChannel, company: CompanyProfile) => {
        const channelCompanies = await ChatChannelAccessService.listCompanies(channel.project);

        if (ChatHelpers.companyIds(channel.id, channelCompanies).includes(company.id)) {
            return;
        }

        InternalTrackingService.track(
            'diagram.chat.invite-company.request',
            {
                channel: channel.name,
                company: company.name,
            },
            {
                notifySlack: true,
            },
        );

        ChatService.openAskCompanyModal({ company, channel });
    },

    setOptimisticChannels: (projectId: string, updater: (channels: DiagramChatChannel[]) => DiagramChatChannel[]) =>
        mutate<Awaited<ReturnType<typeof ChatService.getChannels>>>(
            ChatHelpers.swr.channels(projectId),
            (data) => {
                if (!data) {
                    return {
                        channels: updater([]),
                    };
                }

                return {
                    ...data,
                    channels: updater(data.channels),
                };
            },
            { revalidate: false },
        ).catch((error) => {
            console.error('Failed to set optimistic channels', error);
        }),

    openUpdateChannelModal: (channel: DiagramChatChannel) => {
        openContextModal({
            modal: 'updateChatChannel',
            innerProps: {
                channel,
            },
            withCloseButton: false,
        });
    },

    openChannelAccessModal: (channel: DiagramChatChannel) => {
        openContextModal({
            modal: 'channelAccess',
            innerProps: {
                channel,
            },
            withCloseButton: false,
        });
    },

    updateChannel: async (channel: Pick<DiagramChatChannel, 'id' | 'name'>) => {
        const response = await ApiService.patch<{
            doc: DiagramChatChannel;
        }>(
            `${publicConfig.urls.api}/diagramChatChannels/${channel.id}`,
            {
                name: channel.name,
            },
            { throwError: true },
        );

        return response.doc;
    },

    inviteEngineeringSupport: async (channel: DiagramChatChannel) => {
        await ApiService.post(`${publicConfig.urls.api}/diagramChatChannels/${channel.id}/invite-engineering-support`);

        await ChatChannelAccessService.refresh(channel.project);
    },

    inviteCompany: async (channel: DiagramChatChannel, company: Manufacturer) => {
        await ApiService.post(
            `${publicConfig.urls.api}/diagramChatChannels/${channel.id}/invite-company`,
            { companyId: company.id },
            { throwError: true },
        );

        await ChatChannelAccessService.refresh(channel.project);
    },

    viewChannel: async (channelId: DiagramChatChannel['id']) => {
        if (ImpersonationService.isImpersonating()) {
            return;
        }

        await ApiService.post(`${publicConfig.urls.api}/diagramChatChannels/${channelId}/view`);
    },

    activateChannel: (channelId: DiagramChatChannel['id'], channelType?: DiagramChatChannelType) => {
        chatState.channel = channelId;
        chatState.thread = null;
        ProjectCollaboratorService.clearSuggestedCollaboratorEmail();

        InternalTrackingService.track('diagram.chat.channel.open', {
            channel: channelId,
            channelType,
        });

        SidebarService.open(SidebarType.CHAT);
    },

    deactivateChannel: () => {
        chatState.channel = null;
        chatState.thread = null;
        ProjectCollaboratorService.clearSuggestedCollaboratorEmail();
    },

    getMessages: async (channel: DiagramChatChannel['id'], limit = ChatHelpers.defaults.messageLimit) => {
        const response = await PayloadSDK.find({
            collection: 'diagramChatMessages',
            where: {
                channel: {
                    equals: channel,
                },
                parent: {
                    exists: false,
                },
            },
            joins: {
                thread: {
                    limit: 999,
                    count: true,
                    sort: 'createdAt',
                },
            },
            limit,
        });

        return {
            ...response,
            docs: response.docs as DiagramChatMessage[],
        };
    },

    loadMoreMessages: (channel: DiagramChatChannel['id']) => {
        const limit = chatState.limits[channel] || ChatHelpers.defaults.messageLimit;

        chatState.limits[channel] = limit + 10;
    },

    refreshMessages: async (channelId: DiagramChatChannel['id']) => {
        await mutate(ChatHelpers.swr.messages(channelId));
    },

    setOptimisticMessages: (channelId: string, updater: (messages: DiagramChatMessage[]) => DiagramChatMessage[]) => {
        const limit = chatState.limits[channelId] || ChatHelpers.defaults.messageLimit;

        return mutate(
            ChatHelpers.swr.messages(channelId, limit),
            (data) => {
                if (!data) {
                    return data;
                }

                return {
                    ...data,
                    docs: updater(data.docs),
                };
            },
            {
                revalidate: false,
            },
        );
    },

    create: async (
        args: BaseMessageArgs & {
            content: DiagramChatMessage['content'][number];
        },
    ) => {
        return await PayloadSDK.create({
            collection: 'diagramChatMessages',
            data: {
                project: args.projectId,
                diagram: args.diagramId,
                channel: args.channelId,
                parent: args.parentId || '',
                content: [args.content],
                // Autofilled on the server
                team: '',
                createdByCompany: args.createdByCompany,
            },
        });
    },

    createMessage: async (
        args: BaseMessageArgs & {
            content: string;
            files: any;
            channelType?: DiagramChatChannelType;
            isFirstMessage?: boolean;
        },
    ) => {
        const message = await ChatService.create({
            ...args,
            content: {
                blockType: 'message',
                content: args.content,
                files: args.files,
            },
        });

        if (PermissionService.can(PermissionDiagramGeneral.SHARE)) {
            ProjectCollaboratorService.setSuggestedCollaboratorEmail(ChatService.findInvitableEmail(args.content));
        }

        InternalTrackingService.track('diagram.chat.message.create', {
            project: args.projectId,
            diagram: args.diagramId,
            channel: args.channelId,
        });

        if (args.isFirstMessage) {
            InternalTrackingService.track('diagram.chat.message.first', {
                project: args.projectId,
                diagram: args.diagramId,
                channel: args.channelId,
                channelType: args.channelType,
            });

            if (chatState.prefillMessage) {
                InternalTrackingService.track('diagram.chat.support.message-sent-with-prefill', {
                    project: args.projectId,
                    diagram: args.diagramId,
                    channel: args.channelId,
                    channelType: args.channelType,
                });
                ChatService.clearPrefillMessage();
            }
        }

        ChatService.setOptimisticMessages(args.channelId, (messages: any) => {
            return args.parentId
                ? messages.map((existing: any) => {
                      return existing.id === args.parentId
                          ? {
                                ...existing,
                                thread: {
                                    ...existing.thread,
                                    docs: appendIfMissing(existing.thread.docs ?? [], message),
                                },
                            }
                          : existing;
                  })
                : appendIfMissing(messages, message);
        });
    },

    findInvitableEmail: (content: string) => {
        const text = TipTapHelpers.getTextFragments(JSON.parse(content)).join('\n');
        const email = text.match(/[\w.%+-]+@[a-z\d.-]+\.[a-z]{2,}/i)?.[0].toLowerCase() ?? null;

        return email === currentUserState.user?.email?.toLowerCase() ? null : email;
    },

    summarizeChannel: async (args: BaseMessageArgs) => {
        const message = await ChatService.create({
            ...args,
            content: {
                blockType: 'summary',
                generating: true,
            },
        });

        InternalTrackingService.track('diagram.chat.message.summarize', {
            project: args.projectId,
            diagram: args.diagramId,
            channel: args.channelId,
        });

        await ChatService.setOptimisticMessages(args.channelId, (messages: any) => {
            return [...messages, message];
        });

        return message;
    },

    updateMessage: async (id: DiagramChatMessage['id'], content: string, files: any) => {
        await PayloadSDK.update({
            collection: 'diagramChatMessages',
            id,
            data: {
                content: [
                    {
                        blockType: 'message',
                        content,
                        files,
                    },
                ],
            },
        });
    },

    credits: {
        estimate: async (args: BaseMessageArgs & { credits: number; description: string }) => {
            return await ChatService.create({
                ...args,
                content: {
                    blockType: 'credits.estimate',
                    credits: args.credits,
                    description: args.description,
                },
            });
        },

        accept: async (args: BaseMessageArgs & { estimate: string }) => {
            return await ChatService.create({
                ...args,
                content: {
                    blockType: 'credits.accept',
                    estimate: args.estimate,
                },
            });
        },

        decline: async (args: BaseMessageArgs & { estimate: string }) => {
            return await ChatService.create({
                ...args,
                content: {
                    blockType: 'credits.decline',
                    estimate: args.estimate,
                },
            });
        },

        charge: async (args: BaseMessageArgs & { credits: number; description: string }) => {
            return await ChatService.create({
                ...args,
                content: {
                    blockType: 'credits.charge',
                    credits: args.credits,
                    description: args.description,
                },
            });
        },
    },

    activateThread: (messageId: DiagramChatMessage['id']) => {
        chatState.thread = messageId;

        InternalTrackingService.track('diagram.chat.thread.open', {
            thread: messageId,
            channel: chatState.channel,
        });
    },

    deactivateThread: () => {
        chatState.thread = null;
    },

    adminList: async (data: Partial<DiagramChatChannelListParams>) => {
        const response = await ApiService.get<ListResponse<DiagramChatChannel>>(
            `${publicConfig.urls.api}/diagramChatChannels/admin-list?query=${JSON.stringify(data)}`,
        );

        return response;
    },

    prefillMessage: (message: string | object) => {
        chatState.prefillMessage = message;
    },

    clearPrefillMessage: () => {
        chatState.prefillMessage = '';
    },

    showAccessPopover: () => {
        chatState.showAccessPopover = true;
    },

    hideAccessPopover: () => {
        chatState.showAccessPopover = false;
    },
};

// The realtime refresh triggered by our own message can land before the create request resolves.
const appendIfMissing = <Message extends { id: string }>(messages: Message[], message: Message) =>
    messages.some((existing) => existing.id === message.id) ? messages : [...messages, message];

export { ChatService };
